export * from './theaWebSubmitGiftFlow';
export * from './theaWebRecordActivity';
export * from './theaWebRecordAddedLink';
export * from './theaWebMutateRecommendation';
export * from './theaWebCreateRecipient';
export * from './theaWebUpdateRecipient';
export * from './theaWebMergeGiftFlow';
export * from './theaWebMintMergeToken';
export * from './theaWebLogEvents';
export * from './theaWebCreateShareLink';
export * from './theaWebGetSharedBoard';
export * from './theaWebRevokeShareLink';
export * from './theaWebSimilarProducts';
export * from './theaWebSearchGifts';
export * from './theaWebSearchGuides';
export * from './theaWebCollectionGifts';
export * from './theaWebTrackSignup';
export * from './theaWebCaptureEmail';
export * from './theaWebSyncTimezone';
export * from './theaWebMintResumeToken';
export * from './theaWebRedeemResumeToken';
export * from './theaWebRequestEmailOtp';
export * from './theaWebVerifyEmailOtp';
export * from './theaWebGetClaimPreview';
export * from './theaWebClaimBoard';
export * from './theaWebRequestAccountDeletion';
export * from './theaWebConfirmAccountDeletion';
export * from './theaWebResolveConsentGeo';
export * from './theaWebGetLaunchFlags';
export * from './theaWebGetBoardUsage';

// Callable function names — keep in sync with `thea-serverless/functions/main.py`.
//
// EVERY NAME HERE MUST BE A FUNCTION THAT ACTUALLY EXISTS in main.py. This list
// used to carry `similarProducts: 'theaWebSimilarProducts'`, a service that had
// been pruned from prod years' worth of deploys ago; the name being here made
// it look deployed, and the rail's fallback spent a 404 round-trip on it before
// rendering blank. Removed in THE-482, which made it a dispatcher op. A name
// kept for stale-bundle back-compat (getClaimPreview, claimBoard) is fine —
// those functions are still deployed. A name kept for nothing is a trap.
export const THEA_WEB_CALLABLES = {
  submitGiftFlow: 'theaWebSubmitGiftFlow',
  recordActivity: 'theaWebRecordActivity',
  mutateRecommendation: 'theaWebMutateRecommendation',
  updateRecipient: 'theaWebUpdateRecipient',
  updateAlert: 'theaWebUpdateAlert',
  mergeGiftFlow: 'theaWebMergeGiftFlow',
  mintMergeToken: 'theaWebMintMergeToken',
  logEvents: 'theaWebLogEvents',
  createShareLink: 'theaWebCreateShareLink',
  getSharedBoard: 'theaWebGetSharedBoard',
  revokeShareLink: 'theaWebRevokeShareLink',
  trackSignup: 'theaWebTrackSignup',
  captureEmail: 'theaWebCaptureEmail',
  syncTimezone: 'theaWebSyncTimezone',
  mintResumeToken: 'theaWebMintResumeToken',
  redeemResumeToken: 'theaWebRedeemResumeToken',
  requestEmailOtp: 'theaWebRequestEmailOtp',
  verifyEmailOtp: 'theaWebVerifyEmailOtp',
  // v4 guest→account claim flow (#152). Dispatcher ops since 2026-09-01;
  // these dedicated names remain only for stale-bundle back-compat.
  getClaimPreview: 'theaWebGetClaimPreview',
  claimBoard: 'theaWebClaimBoard',
} as const;

export type TheaWebCallableName = (typeof THEA_WEB_CALLABLES)[keyof typeof THEA_WEB_CALLABLES];

// ---------------------------------------------------------------------------
// theaWeb dispatcher (2026-07 cost audit)
//
// The lean callables above are now served by ONE deployed function —
// `theaWeb` — that routes `{op, payload}` envelopes to the same backend
// handlers. One warm instance covers every latency-critical path instead of
// one $6.60/mo warm service per endpoint. `callables.ts` builds its wrappers
// from THEA_WEB_OPS; the dedicated names above remain only for the raw
// eventSink path (logEvents) and stale-bundle back-compat. Since THE-482 there
// is no non-routed wrapper left.
// ---------------------------------------------------------------------------

export const THEA_WEB_DISPATCHER = 'theaWeb';

// Op names — MUST mirror `TheaWebOp` in
// thea-serverless/functions/theaWebMutations/theaWebDispatcher.py.
// Each repo carries a unit test asserting its side of the mirror
// (FE: callables.test.ts, BE: test_dispatcher.py) — update BOTH lists
// and both tests when adding an op.
export const THEA_WEB_OPS = [
  'updateHolidayPrefs',
  'resolveAppleAccount',
  'submitGiftFlow',
  'recordActivity',
  // Add a link from any store (2026-10-21, THE-1084 PR2). A `kind: 'LINK'`
  // sibling of recordActivity's giftActivity write — one recipient, no
  // product lookup, no Sovrn affiliate resolution (structural). On the WARM
  // door for the same reason as recordActivity: it fires mid-gesture, right
  // after the user taps Save in the Add-a-link sheet.
  'recordAddedLink',
  // Add a person without the quiz (2026-10-01, THE-698 WS1). The first op
  // that can create a recipient at all: the rules deny client writes on that
  // path and submitGiftFlow was the only other writer. Lean — one Firestore
  // transaction, no secrets, no model call — and on the WARM door because it
  // sits mid-gesture: the user has already tapped Save and the new person's
  // id has to come back before recordActivity can be called with it.
  'createRecipient',
  'updateRecipient',
  'updateAlert',
  'mergeGiftFlow',
  'mintMergeToken',
  'logEvents',
  'createShareLink',
  'getSharedBoard',
  'revokeShareLink',
  'trackSignup',
  'captureEmail',
  'syncTimezone',
  'recordLegalAgreement',
  'mintResumeToken',
  'redeemResumeToken',
  'requestEmailOtp',
  'verifyEmailOtp',
  'getClaimPreview',
  'claimBoard',
  // Self-serve account deletion (GDPR Art. 17). TWO ops on purpose: the
  // request half mails a confirmation code and can destroy nothing, so the
  // button is incapable of deleting an account on one click.
  'requestAccountDeletion',
  'confirmAccountDeletion',
  // Cookie-consent geo gate (2026-09-02): "does this visitor need the
  // banner?" Called pre-consent by every visitor, before any tag may fire.
  'resolveConsentGeo',
  // "More like this" rail fallback (2026-09-22, THE-482). The rail's SECOND
  // source: loadSimilarProducts reads the materialized doc first and only
  // calls this when a product has no rail yet. It rides the dispatcher
  // because a cold KNN standing in for a single Firestore read is a visible
  // pause, and because an op cannot be pruned on its own — which is what
  // happened to the dedicated service this replaces.
  'similarProducts',
  // Gift Guide Hub search bar (2026-09-25, THE-586). Two Typesense queries and
  // NO model call — the hub must not search through the carousel agent, or
  // window shopping becomes the most expensive surface we run. It needs the
  // Typesense secrets, which is a real widening of this service but a narrow
  // one: three env vars at 512MB, not the 1GB + Gemini + agent-deploy bundle
  // that keeps the AI ops behind their own front door.
  'searchGifts',
  'searchGuides',
  // A hub collection's deduped product union (2026-10-05, THE-817 WS0). Needs
  // exactly what searchGifts already mounted — the admin Firestore client and
  // the three TYPESENSE_* env vars — so it adds no secret and no warm
  // instance. It is the collection page's FIRST render, which is the strongest
  // case for the warm door on this surface: a cold start here is the page.
  'collectionGifts',
  // Trebuchet selective-launch flags (2026-10-20). ONE read per auth state:
  // the server evaluates every registered flag for the caller (uid allow/deny
  // lists, percentage rollout, internal-only, requireSignedIn) and returns
  // booleans only — fail closed on any error. Lean: no secrets, no model call,
  // and on the warm door because the fetch gates first renders.
  'getLaunchFlags',
  'getBoardUsage',
] as const;

export type TheaWebOp = (typeof THEA_WEB_OPS)[number];

// ---------------------------------------------------------------------------
// theaWebAI dispatcher (2026-09-01)
//
// The SECOND warm front door, for the two AI heavyweights. They can't ride
// `theaWeb`: they need 1GB / 300s / Gemini + Typesense on the BE, and welding
// that onto the lean service would raise the idle price of the warm path
// everything else depends on and put agent deploy risk in front of OTP verify.
//
// Both were cold until now — an "Update picks" chip-add measured 117.83s cold
// vs 0.16s warm, and the same boot sat in front of quiz results. Note that BE
// gen2 functions run concurrency=1, so one warm instance covers the FIRST
// request at an idle service (the reported bug), not simultaneous ones.
//
// Op names MUST mirror `TheaWebAIOp` in
// thea-serverless/functions/theaWebMutations/theaWebAIDispatcher.py — same
// two-sided mirror test as THEA_WEB_OPS.
// ---------------------------------------------------------------------------

export const THEA_WEB_AI_DISPATCHER = 'theaWebAI';

export const THEA_WEB_AI_OPS = ['getCarouselFeed', 'mutateRecommendation'] as const;

export type TheaWebAIOp = (typeof THEA_WEB_AI_OPS)[number];

/** Wire envelope a dispatcher receives as its callable `data`. Both the lean
 * and AI dispatchers speak the identical shape; `O` names which op set. */
export interface TheaWebDispatchEnvelope<P, O extends string> {
  op: O;
  payload: P;
}

export type TheaWebEnvelope<P> = TheaWebDispatchEnvelope<P, TheaWebOp>;

export type TheaWebAIEnvelope<P> = TheaWebDispatchEnvelope<P, TheaWebAIOp>;
